import type { ChatWSClient } from './ChatWSClient';

/**
 * Module-level registry for per-chat `ChatWSClient` instances.
 *
 * Why not Zustand: `ChatWSClient` carries private mutable state
 * (`status`, `isAuthenticated`, `currentTurn`, reconnect counters) that
 * changes outside React's knowledge. Storing the instance in Zustand —
 * even when no current consumer reads it reactively — invites a future
 * landmine where a selector returns a "stale" client because the
 * reference identity never changes even though the inner state does.
 * Keeping the registry outside the React tree makes the
 * non-reactive nature explicit; reactive consumers should go through
 * `ChatWSClient.onStatusChange` (or a `useSyncExternalStore` adapter
 * built on top of it) instead.
 *
 * Why a resolver instead of polling: `useCreateChatMutation.onSuccess`
 * fires `submitMessage` synchronously, but `ChatInterface` (and
 * therefore `useChatWSConnection`) only mounts on the next React
 * render. `waitForClient` resolves as soon as `setClient` is called,
 * eliminating the previous 25ms × 5s busy-poll (and the
 * `vi.useFakeTimers` footgun it created in tests).
 *
 * Auth readiness is intentionally NOT part of this contract — that's
 * owned by `ChatWSClient.connect()` and awaited inside `submit` /
 * `resumeToolUse`.
 */

/** Default timeout for `waitForClient`. Backstop for the mount race. */
export const CHAT_WS_CLIENT_WAIT_TIMEOUT_MS = 5_000;

interface Entry {
  client: ChatWSClient | null;
  /**
   * Resolves with the registered client. Re-created lazily after
   * `clearClient` so callers that race against the next registration
   * pick up the fresh promise rather than an already-resolved stale one.
   */
  ready: Promise<ChatWSClient>;
  resolveReady: (client: ChatWSClient) => void;
}

const registry = new Map<string, Entry>();

interface ChatWSDebugConsole {
  drop: (chatID: string) => boolean;
  dropAll: () => number;
  list: () => string[];
}

declare global {
  interface Window {
    __CIRCUIT_CHAT_WS_DEBUG__?: ChatWSDebugConsole;
  }
}

function makeEntry(): Entry {
  let resolveReady!: (client: ChatWSClient) => void;
  const ready = new Promise<ChatWSClient>((resolve) => {
    resolveReady = resolve;
  });
  return { client: null, ready, resolveReady };
}

function ensureEntry(chatID: string): Entry {
  let entry = registry.get(chatID);
  if (!entry) {
    entry = makeEntry();
    registry.set(chatID, entry);
  }
  return entry;
}

/**
 * Register a client for a chat. Resolves any pending `waitForClient`
 * promises for this chat ID. The caller (the connection hook) owns the
 * client's lifecycle — re-registering without first calling
 * `clearClient` is a programming error and the existing client will be
 * overwritten.
 */
export function setClient(chatID: string, client: ChatWSClient): void {
  const entry = ensureEntry(chatID);
  entry.client = client;
  entry.resolveReady(client);
}

/**
 * Synchronous lookup. Returns null if no client is registered for the
 * chat. Used on hot paths like `cancel()` where we explicitly want to
 * be a no-op rather than block on a mount race.
 */
export function getClient(chatID: string): ChatWSClient | null {
  return registry.get(chatID)?.client ?? null;
}

/**
 * Remove the client registration. A subsequent `setClient` will create
 * a fresh entry (and therefore a fresh `ready` promise) so any
 * `waitForClient` racing after the clear waits for the next mount
 * rather than resolving with the disconnected client.
 */
export function clearClient(chatID: string): void {
  registry.delete(chatID);
}

/**
 * Resolve as soon as a client is registered for `chatID`, or with
 * `null` if no client appears within `timeoutMs`. Existing
 * registrations resolve synchronously on the next microtask.
 */
export async function waitForClient(
  chatID: string,
  timeoutMs: number = CHAT_WS_CLIENT_WAIT_TIMEOUT_MS,
): Promise<ChatWSClient | null> {
  const existing = getClient(chatID);
  if (existing) return existing;

  const entry = ensureEntry(chatID);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });

  try {
    return await Promise.race([entry.ready, timeout]);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

/**
 * Test-only helper: drop every registered client. Use in `afterEach`
 * to keep modules isolated across cases.
 */
// eslint-disable-next-line no-underscore-dangle -- intentional __prefix for test-only helpers
export function __resetChatWSClientRegistry(): void {
  registry.clear();
}

function registeredChatIDs(): string[] {
  return [...registry.entries()]
    .filter(([, entry]) => entry.client !== null)
    .map(([chatID]) => chatID);
}

function debugDropChatWS(chatID: string): boolean {
  return getClient(chatID)?.debugSimulateNetworkDrop() ?? false;
}

function debugDropAllChatWS(): number {
  let dropped = 0;
  for (const chatID of registeredChatIDs()) {
    if (debugDropChatWS(chatID)) dropped += 1;
  }
  return dropped;
}

function installChatWSDebugConsole(): void {
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line no-underscore-dangle -- debug console attached to window for dev tooling
  window.__CIRCUIT_CHAT_WS_DEBUG__ = {
    drop: debugDropChatWS,
    dropAll: debugDropAllChatWS,
    list: registeredChatIDs,
  };
}

installChatWSDebugConsole();
